import { useCallback, useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H4 } from 'UIComponents/elements/headings';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import CompletedTask from './task/CompletedTask';
import SkippedTask from './task/SkippedTask';
import UncompletedTask from './task/UncompletedTask';
import TasksCompletedCard from './TasksCompletedCard';
import { useTracker } from '../../hooks/tracking';
import emptyFn from '../../utils/emptyFn';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const mapTasks = (taskKeys, taskMap, callback) => {
  return taskKeys.map(taskKey => taskMap[taskKey] && callback(taskMap[taskKey]));
};
const ChecklistTasks = ({
  taskData,
  expandFirstUncompletedTask = true,
  setShowActionedTasks = emptyFn,
  showActionedTasks,
  TasksCompletedComponent = TasksCompletedCard,
  simplifiedTasksExpanded = false,
  simplifiedTaskList = false,
  groupKey
}) => {
  const {
    isLoading,
    taskMap = {},
    taskProgress,
    hasErrored
  } = taskData;
  const {
    tasksCompleted = [],
    tasksSkipped = [],
    tasksUncompleted = []
  } = taskProgress || {};
  const currentGroupTasksUncompleted = Object.keys(taskMap).filter(task => tasksUncompleted.includes(task));
  const firstUncompletedTask = currentGroupTasksUncompleted[0];
  const [expandedTask, setExpandedTask] = useState();
  const {
    trackTaskInteraction
  } = useTracker();
  const getTaskProps = useCallback(task => {
    const {
      durationInMinutes,
      key
    } = task;
    return {
      expanded: expandedTask === key,
      durationInMinutes,
      key,
      onExpandedChange(isExpanded, taskState) {
        const action = `${isExpanded ? 'expand' : 'close'} task`;
        setExpandedTask(isExpanded && key);
        trackTaskInteraction(action, key, {
          taskState
        });
      },
      task: Object.assign({}, task, {
        handholdingGroupKeyOverride: groupKey
      })
    };
  }, [expandedTask, trackTaskInteraction, groupKey]);

  // expand first uncompleted task by default
  useEffect(() => {
    if (!isLoading && firstUncompletedTask && expandFirstUncompletedTask) {
      setExpandedTask(firstUncompletedTask);
    }
  }, [isLoading, firstUncompletedTask, expandFirstUncompletedTask]);
  const tasksUncompletedCount = currentGroupTasksUncompleted.length;
  // hide actioned task when all tasks are completed
  useEffect(() => {
    if (!isLoading && tasksUncompletedCount === 0) {
      setShowActionedTasks(false);
    }
  }, [isLoading, setShowActionedTasks, tasksUncompletedCount]);
  if (hasErrored) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      "data-test-id": "checklist-error",
      title: /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "setup-guide-components.checklist.error.loading.title"
        })
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "setup-guide-components.checklist.error.loading.message"
      })
    });
  }
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "setup-guide-components.checklist.loading"
      }),
      minHeight: 130,
      showLabel: true
    });
  }
  const tasks = taskMap;
  if (simplifiedTaskList) {
    // show only the first task expanded, hide the rest behind an accordion
    return /*#__PURE__*/_jsxs("div", {
      "data-onboarding": "checklist-tasks",
      children: [/*#__PURE__*/_jsx(AccordionTransition, {
        open: tasksUncompletedCount === 0,
        children: TasksCompletedComponent && /*#__PURE__*/_jsx(TasksCompletedComponent, {})
      }), mapTasks([currentGroupTasksUncompleted[0]], tasks, task => /*#__PURE__*/_jsx(UncompletedTask, Object.assign({}, getTaskProps(task)))), /*#__PURE__*/_jsxs(AccordionTransition, {
        open: simplifiedTasksExpanded,
        children: [mapTasks(currentGroupTasksUncompleted.slice(1), tasks, task => /*#__PURE__*/_jsx(UncompletedTask, Object.assign({}, getTaskProps(task)))), mapTasks(tasksSkipped, tasks, task => /*#__PURE__*/_jsx(SkippedTask, Object.assign({}, getTaskProps(task)))), mapTasks(tasksCompleted, tasks, task => /*#__PURE__*/_jsx(CompletedTask, Object.assign({}, getTaskProps(task))))]
      })]
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "checklist-tasks",
    children: [/*#__PURE__*/_jsx(AccordionTransition, {
      open: tasksUncompletedCount === 0,
      children: TasksCompletedComponent && /*#__PURE__*/_jsx(TasksCompletedComponent, {})
    }), mapTasks(currentGroupTasksUncompleted, tasks, task => /*#__PURE__*/_jsx(UncompletedTask, Object.assign({}, getTaskProps(task)))), /*#__PURE__*/_jsxs(AccordionTransition, {
      "data-test-id": "actioned-tasks",
      open: Boolean(showActionedTasks),
      children: [mapTasks(tasksSkipped, tasks, task => /*#__PURE__*/_jsx(SkippedTask, Object.assign({}, getTaskProps(task)))), mapTasks(tasksCompleted, tasks, task => /*#__PURE__*/_jsx(CompletedTask, Object.assign({}, getTaskProps(task))))]
    })]
  });
};
export default ChecklistTasks;